/**
 * PMWorkOrderReviewTab - Tab 3: WO Review & Publish
 *
 * Lifecycle filter: pm_readiness = 'WO_DRAFT'
 *
 * Shows projects that have generated work orders ready for review.
 * Allows PM to:
 * - View SharePoint work order file
 * - Review parsed WO data
 * - Sync from SharePoint
 * - Publish to production
 */

import { useState, useEffect, useCallback } from "react";
import PMWorkOrderReadback from "./PMWorkOrderReadback";

export default function PMWorkOrderReviewTab({ acquireAccessToken, lifecycleFilter = "WO_DRAFT" }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showReadback, setShowReadback] = useState(false);

  // Fetch projects in WO_DRAFT state
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      const params = new URLSearchParams();
      if (lifecycleFilter) {
        params.set("pmReadiness", lifecycleFilter);
      }
      const url = `/api/pm-center/projects/my-projects${params.toString() ? `?${params}` : ""}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch projects");
      }

      const data = await res.json();
      setProjects(data.projects || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching WO Review projects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken, lifecycleFilter]);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Handle publish callback - refresh project list
  const handlePublish = useCallback(() => {
    fetchProjects();
    setSelectedProject(null);
    setShowReadback(false);
  }, [fetchProjects]);

  // Handle closing readback view - return to detail panel
  const handleCloseReadback = useCallback(() => {
    setShowReadback(false);
  }, []);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Empty state
  if (!loading && projects.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">📄</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Work Orders to Review</h2>
          <p className="text-gray-600 max-w-md">
            Projects with generated work orders will appear here when they're ready for review.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Generate a work order from Project Review (Tab 2) to see it here.
          </p>
        </div>
      </div>
    );
  }

  // If showing readback view for selected project
  if (selectedProject && showReadback) {
    return (
      <PMWorkOrderReadback
        acquireAccessToken={acquireAccessToken}
        workOrder={{
          work_order_id: selectedProject.work_order_id,
          id: selectedProject.id,
          quote_number: selectedProject.quote_number,
          project_name: selectedProject.description,
          wo_number: selectedProject.wo_number,
        }}
        onClose={handleCloseReadback}
        onPublish={handlePublish}
      />
    );
  }

  // Project list view
  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Project List */}
      <div className="w-1/2 border-r border-gray-200 overflow-y-auto bg-white">
        <div className="p-3 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">
              Work Orders to Review ({projects.length})
            </h3>
            <button
              onClick={fetchProjects}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-500">
            Loading projects...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-500">
            {error}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {projects.map((project) => (
              <div
                key={project.quote_number}
                onClick={() => setSelectedProject(project)}
                className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">
                        {project.quote_number}
                      </span>
                      {project.wo_number && (
                        <span className="text-xs text-purple-600">
                          {project.wo_number}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {project.customer_name}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">
                      {project.description || "No description"}
                    </p>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <span className="inline-block px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-700">
                      WO Draft
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Ship: {formatDate(project.ship_date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Panel */}
      <div className="w-1/2 flex flex-col bg-gray-50">
        {selectedProject ? (
          // Project detail view with SharePoint link
          <div className="flex-1 overflow-y-auto p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedProject.quote_number}
                </h2>
                <p className="text-gray-600">{selectedProject.customer_name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedProject.description || "No description"}
                </p>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Work Order File Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Work Order File
              </h3>

              {selectedProject.wo_sharepoint_url ? (
                <div className="space-y-3">
                  {/* File Info */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14.17 5L19 9.83V19H5V5h9.17M14.17 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.83c0-.53-.21-1.04-.59-1.41l-4.83-4.83c-.37-.38-.88-.59-1.41-.59zM7 15h10v2H7v-2zm0-4h10v2H7v-2z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {selectedProject.wo_file_name || "Work Order.xlsx"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedProject.wo_sync_status === "synced" ? (
                          <span className="text-green-600">Synced</span>
                        ) : selectedProject.wo_sync_status === "pending" ? (
                          <span className="text-yellow-600">Sync pending</span>
                        ) : (
                          <span className="text-gray-500">Not synced</span>
                        )}
                        {selectedProject.wo_last_synced_at && (
                          <span className="ml-2">
                            Last synced: {new Date(selectedProject.wo_last_synced_at).toLocaleString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Open in SharePoint Button */}
                  <a
                    href={selectedProject.wo_sharepoint_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open Work Order in SharePoint
                  </a>

                  {/* Copy URL Button */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedProject.wo_sharepoint_url);
                      alert("SharePoint URL copied to clipboard!");
                    }}
                    className="flex items-center justify-center gap-2 w-full py-2 px-4 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy SharePoint URL
                  </button>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No SharePoint file linked</p>
                  <p className="text-sm mt-1">Generate the work order from Project Review tab</p>
                </div>
              )}
            </div>

            {/* Project Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <h3 className="font-medium text-gray-900 mb-3">Project Details</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Order #:</span>
                  <span className="ml-2 font-medium">{selectedProject.order_number || "N/A"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Ship Date:</span>
                  <span className="ml-2 font-medium">{formatDate(selectedProject.ship_date)}</span>
                </div>
                <div>
                  <span className="text-gray-500">WO Number:</span>
                  <span className="ml-2 font-medium">{selectedProject.wo_number || "N/A"}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-700">
                    {selectedProject.pm_readiness || "WO_DRAFT"}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setShowReadback(true)}
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Review Work Order Data
              </button>
            </div>
          </div>
        ) : (
          // Empty state
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>Select a work order to review</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
